import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, MONTHS_FR, TECH_FUNCTIONS, OUVREUSE_FUNCTIONS, SPECIFICATIONS, VACATIONS, COMPLEMENTS, HOUR_BRACKETS, sortBesoins } from "@/lib/api";
import TeamRequestModal from "@/components/TeamRequestModal";
import { InformationsGeneralesCard } from "@/components/InformationsGeneralesCard";
import { LogistiqueSections } from "@/components/LogistiqueSections";
import SynthesePage from "@/pages/SynthesePage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Send, ClipboardList, MapPin, Clock, Users, Building2, Ticket, Plus, Trash2, Copy, UserRound, Save, AlertTriangle,
} from "lucide-react";

const HEURES = [];
for (let h = 0; h < 24; h++) for (const m of ["00", "30"]) HEURES.push(`${String(h).padStart(2, "0")}h${m}`);

export default function ConcertDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [concert, setConcert] = useState(null);
  const [interNames, setInterNames] = useState([]);
  const [teamModal, setTeamModal] = useState({ open: false, role: "crew_boss" });
  const [besoins, setBesoins] = useState([]);
  const [salaries, setSalaries] = useState({});
  const [savingBesoins, setSavingBesoins] = useState(false);
  const [besoinsDirty, setBesoinsDirty] = useState(false);
  const [syntheseDirty, setSyntheseDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: inter }, { data: sal }] = await Promise.all([
        api.get(`/concerts/${id}`), api.get("/intermittents"), api.get("/salaries"),
      ]);
      setConcert(c);
      setBesoins(c.besoins || []);
      setBesoinsDirty(false);
      const sm = {}; (sal || []).forEach((s) => { sm[`${s.ville}|||${s.fonction}`] = s; });
      setSalaries(sm);
      const map = Object.fromEntries(inter.map((i) => [i.id, i]));
      setInterNames((c.intermittent_ids || []).map((iid) => map[iid]).filter(Boolean));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Alerte si l'on quitte la page avec des modifications non enregistrées (besoins ou synthèse).
  useEffect(() => {
    if (!(besoinsDirty || syntheseDirty)) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [besoinsDirty, syntheseDirty]);

  const bracketFor = (h) => { const n = parseFloat(h); if (!n) return null; return HOUR_BRACKETS.reduce((a, b) => (Math.abs(b - n) < Math.abs(a - n) ? b : a)); };
  const lookupCachet = (fonction, heures) => {
    if (!concert || !fonction) return "";
    const s = salaries[`${concert.ville}|||${fonction}`]; const br = bracketFor(heures);
    return s && br ? (s[`h${br}`] || "") : "";
  };
  const tariffMissing = (b) => {
    if (!concert || !b.fonction) return false;
    const s = salaries[`${concert.ville}|||${b.fonction}`];
    if (!s) return true;
    if (b.categorie === "Ouvreuses" || b.categorie === "Aides Catering") return !s.taux_horaire;
    return !(s.h4 || s.h6 || s.h8 || s.h10 || s.h12);
  };
  const addBesoin = (categorie) => { setBesoinsDirty(true); setBesoins((b) => [...b, { id: crypto.randomUUID(), categorie, nombre: "1", fonction: categorie === "Aides Catering" ? "Aide Catering" : "", specifications: "", vacation: "", complement: "", heure_debut: "", nombre_heure: "", cachet_brut: categorie === "Aides Catering" ? (salaries[`${concert?.ville}|||Aide Catering`]?.taux_horaire || "") : "" }]); };
  const duplicateBesoin = (bid) => { setBesoinsDirty(true); setBesoins((list) => { const s = list.find((x) => x.id === bid); return s ? [...list, { ...s, id: crypto.randomUUID() }] : list; }); };
  const removeBesoin = (bid) => { setBesoinsDirty(true); setBesoins((list) => list.filter((x) => x.id !== bid)); };
  const updateBesoin = (bid, patch) => { setBesoinsDirty(true); setBesoins((list) => list.map((x) => {
    if (x.id !== bid) return x;
    const next = { ...x, ...patch };
    if (next.categorie === "Techs" && ("fonction" in patch || "nombre_heure" in patch)) {
      const auto = lookupCachet(next.fonction, next.nombre_heure); if (auto) next.cachet_brut = auto;
    }
    if (next.categorie === "Ouvreuses" && "fonction" in patch) {
      const s = salaries[`${concert?.ville}|||${next.fonction}`];
      if (s && s.taux_horaire) next.cachet_brut = s.taux_horaire;
    }
    if (next.categorie === "Aides Catering" && "fonction" in patch) {
      const s = salaries[`${concert?.ville}|||${next.fonction}`];
      if (s && s.taux_horaire) next.cachet_brut = s.taux_horaire;
    }
    return next;
  })); };
  const saveBesoins = async () => {
    setSavingBesoins(true);
    try { await api.put(`/concerts/${id}/besoins`, { besoins }); toast.success("Besoins enregistrés"); setBesoinsDirty(false); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSavingBesoins(false); }
  };
  const persistBesoins = async () => {
    try { await api.put(`/concerts/${id}/besoins`, { besoins }); setBesoinsDirty(false); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); throw err; }
  };
  const openTeamModal = async (role) => {
    try { await persistBesoins(); setTeamModal({ open: true, role }); } catch { /* toast already shown */ }
  };
  const openTeamSpace = async (role) => {
    try {
      await persistBesoins();
      const { data } = await api.get(`/concerts/${id}/team-link`, { params: { role } });
      navigate(`/completer-equipes/${data.token}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (!concert) return <div className="text-muted-foreground">Chargement...</div>;

  const dateLabel = concert.day ? `${concert.day} ${MONTHS_FR[concert.month]} ${concert.year}` : `${MONTHS_FR[concert.month]} ${concert.year}`;
  const Info = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
      <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-sm font-medium">{value || "—"}</div></div>
    </div>
  );

  const renderRow = (b) => {
    const isOuv = b.categorie === "Ouvreuses";
    const isCatering = b.categorie === "Aides Catering";
    const fns = isCatering ? ["Aide Catering"] : isOuv ? OUVREUSE_FUNCTIONS : TECH_FUNCTIONS;
    const vacs = isCatering ? ["Matin", "Soir"] : VACATIONS;
    const missing = tariffMissing(b);
    return (
      <div key={b.id} className={`grid grid-cols-2 md:grid-cols-9 gap-2 items-end border rounded-lg p-2 ${missing ? "border-amber-500/60 bg-amber-500/5" : "border-border"}`} data-testid={`besoin-row-${b.id}`}>
        <div><label className="text-[10px] text-muted-foreground">Nombre</label>
          <Input className="h-8 text-xs" type="number" min="1" value={b.nombre ?? "1"} onChange={(e) => updateBesoin(b.id, { nombre: e.target.value })} data-testid={`besoin-nombre-${b.id}`} /></div>
        <div><label className="text-[10px] text-muted-foreground">Fonction</label>
          <Select value={b.fonction || undefined} onValueChange={(v) => updateBesoin(b.id, { fonction: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid={`besoin-fonction-${b.id}`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{fns.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select></div>
        {!isOuv && !isCatering && (
          <div><label className="text-[10px] text-muted-foreground">Spécifications</label>
            <Select value={b.specifications || undefined} onValueChange={(v) => updateBesoin(b.id, { specifications: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`besoin-spec-${b.id}`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{SPECIFICATIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
        )}
        {!isOuv && (
          <div><label className="text-[10px] text-muted-foreground">Vacation</label>
            <Select value={b.vacation || undefined} onValueChange={(v) => updateBesoin(b.id, { vacation: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`besoin-vacation-${b.id}`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{vacs.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></div>
        )}
        {!isCatering && !isOuv && (
          <div><label className="text-[10px] text-muted-foreground">Complément</label>
            <Select value={b.complement || undefined} onValueChange={(v) => updateBesoin(b.id, { complement: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`besoin-complement-${b.id}`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{COMPLEMENTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        )}
        <div><label className="text-[10px] text-muted-foreground">Get in</label>
          <Select value={b.heure_debut || undefined} onValueChange={(v) => updateBesoin(b.id, { heure_debut: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid={`besoin-heure-${b.id}`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="max-h-60">{HEURES.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-[10px] text-muted-foreground">Nombre d'heures</label>
          <Input className="h-8 text-xs" type="number" value={b.nombre_heure} onChange={(e) => updateBesoin(b.id, { nombre_heure: e.target.value })} data-testid={`besoin-nbh-${b.id}`} /></div>
        <div><label className="text-[10px] text-muted-foreground flex items-center gap-1">{isOuv ? "Coût horaire (€)" : "Cachet brut (€)"}
          {missing && (
            <span title={`Aucun tarif défini pour « ${b.fonction} » à ${concert.ville}. Mettez à jour la page Salaires.`} data-testid={`besoin-tarif-alert-${b.id}`}>
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            </span>
          )}</label>
          <Input className={`h-8 text-xs ${missing ? "border-amber-500/60" : ""}`} value={b.cachet_brut} onChange={(e) => updateBesoin(b.id, { cachet_brut: e.target.value })} data-testid={`besoin-cachet-${b.id}`} /></div>
        <div className="flex items-end gap-1">
          <button onClick={() => duplicateBesoin(b.id)} className="text-muted-foreground hover:text-primary p-1.5" title="Dupliquer" data-testid={`besoin-duplicate-${b.id}`}><Copy className="h-4 w-4" /></button>
          <button onClick={() => removeBesoin(b.id)} className="text-muted-foreground hover:text-destructive p-1.5" title="Supprimer" data-testid={`besoin-remove-${b.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    );
  };

  const techs = sortBesoins(besoins.filter((b) => b.categorie !== "Ouvreuses" && b.categorie !== "Aides Catering"));
  const caterings = sortBesoins(besoins.filter((b) => b.categorie === "Aides Catering"));
  const ouvs = sortBesoins(besoins.filter((b) => b.categorie === "Ouvreuses"));
  const missingPostes = [...new Set(besoins.filter(tariffMissing).map((b) => b.fonction))];

  return (
    <div className="space-y-6">
      <button onClick={() => { if ((besoinsDirty || syntheseDirty) && !window.confirm("Des modifications non enregistrées seront perdues. Quitter la page ?")) return; navigate("/"); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-planning">
        <ArrowLeft className="h-4 w-4" /> Retour au planning
      </button>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,56rem)_minmax(16rem,1fr)]">
        <div className="rounded-2xl border border-border bg-card p-6 self-start" data-testid="concert-detail-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-primary">{dateLabel}</div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">{concert.concert}</h1>
            </div>
            {concert.contrat && <span className="rounded bg-sky-500/10 text-sky-400 border border-sky-500/20 px-2 py-1 text-xs">{concert.contrat}</span>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 mt-6">
            <Info icon={Clock} label="Heure" value={concert.heure} />
            <Info icon={Building2} label="Salle" value={concert.salle} />
            <Info icon={MapPin} label="Ville" value={concert.ville} />
            <Info icon={Users} label="Prod nationale" value={concert.prod} />
            <Info icon={Ticket} label="Config" value={concert.config} />
            <Info icon={Ticket} label="Jauge" value={concert.jauge} />
          </div>
          {concert.commentaires && <div className="mt-5 text-sm text-muted-foreground border-l-2 border-primary/40 pl-3">{concert.commentaires}</div>}
        </div>
        <InformationsGeneralesCard key={concert.id} concert={concert} onFicheChange={(f) => setConcert((c) => ({ ...c, fiche_technique: f }))} />
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 w-full" data-testid="besoins-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold">Besoins de la production</h2>
          <Button onClick={saveBesoins} disabled={savingBesoins} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="save-besoins-button">
            <Save className="h-4 w-4 mr-2" />{savingBesoins ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>

        {missingPostes.length > 0 && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3" data-testid="tarif-missing-banner">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-amber-400">
                {missingPostes.length} poste{missingPostes.length > 1 ? "s" : ""} sans tarif défini pour {concert.ville}
              </p>
              <p className="text-amber-200/80 mt-0.5">
                {missingPostes.join(", ")} — pensez à renseigner le cachet dans la page Salaires pour un calcul automatique.
              </p>
            </div>
          </div>
        )}

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-primary">Techs</h3>
            <Button size="sm" variant="outline" onClick={() => addBesoin("Techs")} data-testid="add-besoin-techs"><Plus className="h-4 w-4 mr-1.5" />Ajouter</Button>
          </div>
          <div className="space-y-2">
            {techs.length === 0 && <p className="text-sm text-muted-foreground">Aucun besoin technique.</p>}
            {techs.map(renderRow)}
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-semibold text-primary">Aides Catering</h3>
              <Button size="sm" variant="outline" onClick={() => addBesoin("Aides Catering")} data-testid="add-besoin-catering"><Plus className="h-4 w-4 mr-1.5" />Ajouter</Button>
            </div>
            <div className="space-y-2">
              {caterings.length === 0 && <p className="text-sm text-muted-foreground">Aucune aide catering.</p>}
              {caterings.map(renderRow)}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => openTeamSpace("crew_boss")} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="team-space-techs">
              <ClipboardList className="h-4 w-4 mr-2" />Espace de demande des équipes
            </Button>
            <Button onClick={() => openTeamModal("crew_boss")} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="send-crew-request-button">
              <Send className="h-4 w-4 mr-2" />Envoyer la demande d'équipe au Crew Boss
            </Button>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-primary">Ouvreuses</h3>
            <Button size="sm" variant="outline" onClick={() => addBesoin("Ouvreuses")} data-testid="add-besoin-ouvreuses"><Plus className="h-4 w-4 mr-1.5" />Ajouter</Button>
          </div>
          <div className="space-y-2">
            {ouvs.length === 0 && <p className="text-sm text-muted-foreground">Aucun besoin ouvreuses.</p>}
            {ouvs.map(renderRow)}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => openTeamSpace("chef_hotesse")} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="team-space-ouvreuses">
              <ClipboardList className="h-4 w-4 mr-2" />Espace de demande des équipes
            </Button>
            <Button onClick={() => openTeamModal("chef_hotesse")} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="send-hotesse-request-button">
              <UserRound className="h-4 w-4 mr-2" />Envoyer la demande d'équipe à la chef hôtesse
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 w-full" data-testid="synthese-detail-embedded">
        <SynthesePage embedded besoins={besoins} onDirtyChange={setSyntheseDirty} />
      </div>

      <LogistiqueSections key={concert.id} concertId={id} initial={concert.logistique} />

      <TeamRequestModal open={teamModal.open} onOpenChange={(o) => setTeamModal((t) => ({ ...t, open: o }))} concertId={id} role={teamModal.role} />
    </div>
  );
}
